import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { isAuthenticatedNavigation } from './dashboard/navigation-history';
import { AuthShell } from './auth/AuthShell';
import { AuthApiError, authApi, type SessionViewerResult } from './auth/api';
import {
  executeRecaptchaChallenge,
  RecaptchaBrowserUnavailableError,
} from './auth/recaptcha-browser';
import {
  loginWithPasskey,
  PasskeyCeremonyCancelledError,
  PasskeyUnavailableError,
  supportsPasskeyLogin,
} from './auth/passkey-browser';
import { DashboardShell } from './dashboard/DashboardShell';

type AuthView = 'LOGIN' | 'TOTP' | 'FORGOT' | 'RESET' | 'SIGNED_IN';

function routeView(): AuthView {
  if (window.location.pathname === '/mot-de-passe-oublie') return 'FORGOT';
  if (window.location.pathname === '/reinitialiser-mot-de-passe') return 'RESET';
  return 'LOGIN';
}

function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

async function withAdaptiveCaptcha<T>(
  operation: (captchaToken?: string) => Promise<T>,
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (
      !(error instanceof AuthApiError) ||
      error.code !== 'AUTH_CAPTCHA_REQUIRED' ||
      error.captcha === undefined
    ) {
      throw error;
    }
    const token = await executeRecaptchaChallenge(error.captcha);
    return await operation(token);
  }
}

function isCaptchaFailure(error: unknown): boolean {
  return (
    error instanceof RecaptchaBrowserUnavailableError ||
    (error instanceof AuthApiError && error.code === 'AUTH_CAPTCHA_REQUIRED')
  );
}

function LoginView({
  remember,
  onRememberChange,
  onTotp,
  onAuthenticated,
}: {
  readonly remember: boolean;
  readonly onRememberChange: (remember: boolean) => void;
  readonly onTotp: () => void;
  readonly onAuthenticated: (csrfToken: string) => Promise<void>;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyNotice, setPasskeyNotice] = useState<string | null>(null);
  const passkeySupported = useMemo(() => supportsPasskeyLogin(), []);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    setPasskeyNotice(null);
    try {
      await withAdaptiveCaptcha((captchaToken) =>
        authApi.beginLogin(email, password, captchaToken),
      );
      onTotp();
    } catch (caught) {
      setError(
        isCaptchaFailure(caught)
          ? 'La vérification de sécurité n’a pas pu être validée. Réessayez.'
          : 'Adresse e-mail, mot de passe ou accès invalide.',
      );
    } finally {
      setPending(false);
    }
  }

  async function usePasskey(): Promise<void> {
    if (!passkeySupported || pending || passkeyPending) return;

    setPasskeyPending(true);
    setError(null);
    setPasskeyNotice(null);
    try {
      const result = await loginWithPasskey(remember);
      await onAuthenticated(result.csrfToken);
    } catch (caught) {
      if (caught instanceof PasskeyCeremonyCancelledError) {
        setPasskeyNotice('Connexion par passkey annulée ou expirée.');
      } else if (caught instanceof PasskeyUnavailableError) {
        setPasskeyNotice('La connexion par passkey n’est pas disponible sur ce navigateur.');
      } else {
        setError('La connexion par passkey n’a pas pu être effectuée.');
      }
    } finally {
      setPasskeyPending(false);
    }
  }

  return (
    <AuthShell
      title="Forgez des projets plus affûtés, chaque jour."
      intro="Pilotage de projets, de FAQ clients et gestion du support grâce à un outil performant et fiable."
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label className="auth-field">
          <span>Adresse e-mail</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </label>
        <label className="auth-field">
          <span>Mot de passe</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
        </label>
        <div className="auth-form__options">
          <label className="auth-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => onRememberChange(event.currentTarget.checked)}
            />
            <span>Se souvenir de cet appareil</span>
          </label>
          <button
            className="auth-link"
            type="button"
            onClick={() => navigate('/mot-de-passe-oublie')}
          >
            Mot de passe oublié ?
          </button>
        </div>
        {error ? (
          <p className="auth-message tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        {passkeyNotice ? (
          <p className="auth-message" role="status">
            {passkeyNotice}
          </p>
        ) : null}
        <button
          className="auth-button auth-button--primary"
          type="submit"
          disabled={pending || passkeyPending}
        >
          <span aria-hidden="true">↪</span>
          {pending ? 'Vérification…' : 'Se connecter'}
        </button>
        {passkeySupported ? (
          <button
            className="auth-button auth-button--secondary"
            type="button"
            disabled={pending || passkeyPending}
            onClick={() => void usePasskey()}
          >
            <span aria-hidden="true">⚿</span>
            {passkeyPending ? 'Ouverture…' : 'Utiliser une passkey'}
          </button>
        ) : null}
      </form>
      <aside className="auth-security-note">
        <span className="auth-security-note__icon" aria-hidden="true">
          ◇
        </span>
        <span>
          <strong>Vos données confidentielles sont protégées</strong>
          <small>
            Tosho utilise les dernières technologies de chiffrement et d’identification.
          </small>
        </span>
      </aside>
    </AuthShell>
  );
}

function TotpView({
  requestExtendedSession,
  onAuthenticated,
  onBack,
}: {
  readonly requestExtendedSession: boolean;
  readonly onAuthenticated: (csrfToken: string) => Promise<void>;
  readonly onBack: () => void;
}): React.JSX.Element {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await authApi.completeTotp(code, requestExtendedSession);
      await onAuthenticated(result.csrfToken);
    } catch {
      setError('Le code est invalide, a expiré ou la session ne peut plus être ouverte.');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Sécurité du compte"
      title="Vérification de sécurité"
      intro="Saisissez le code à 6 chiffres généré par Microsoft Authenticator."
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label className="auth-field">
          <span>Code d’authentification</span>
          <input
            className="auth-field__code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(event) => setCode(event.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          />
        </label>
        {error ? (
          <p className="auth-message tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          className="auth-button auth-button--primary"
          type="submit"
          disabled={pending || code.length !== 6}
        >
          {pending ? 'Vérification…' : 'Vérifier'}
        </button>
        <button className="auth-link auth-link--center" type="button" onClick={onBack}>
          Utiliser une autre méthode
        </button>
      </form>
      <aside className="auth-security-note">
        <span className="auth-security-note__icon" aria-hidden="true">
          ◇
        </span>
        <span>
          <strong>Protection renforcée</strong>
          <small>Cette vérification confirme que vous êtes bien à l’origine de la connexion.</small>
        </span>
      </aside>
    </AuthShell>
  );
}

function ForgotPasswordView(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await withAdaptiveCaptcha((captchaToken) =>
        authApi.requestPasswordReset(email, captchaToken),
      );
      setAccepted(true);
    } catch (caught) {
      setError(
        isCaptchaFailure(caught)
          ? 'La vérification de sécurité n’a pas pu être validée. Réessayez.'
          : 'La demande ne peut pas être traitée pour le moment. Réessayez.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Récupération"
      title="Mot de passe oublié ?"
      intro="Indiquez votre adresse e-mail. Si un compte Tosho correspondant existe, un lien de réinitialisation vous sera envoyé."
    >
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label className="auth-field">
          <span>Adresse e-mail</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </label>
        {accepted ? (
          <p className="auth-message tosho-feedback is-success" role="status">
            Si cette adresse est connue, le message de récupération a été envoyé.
          </p>
        ) : null}
        {error ? (
          <p className="auth-message tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : null}
        <button className="auth-button auth-button--primary" type="submit" disabled={pending}>
          {pending ? 'Envoi…' : 'Envoyer le lien'}
        </button>
        <button className="auth-link auth-link--center" type="button" onClick={() => navigate('/')}>
          Retour à la connexion
        </button>
      </form>
    </AuthShell>
  );
}

function ResetPasswordView(): React.JSX.Element {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError('Les deux mots de passe doivent être identiques.');
      return;
    }
    setPending(true);
    try {
      await authApi.completePasswordReset(token, password);
      setDone(true);
    } catch {
      setError(
        'Le lien est invalide, expiré ou le nouveau mot de passe ne respecte pas la politique de sécurité.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Récupération"
      title="Nouveau mot de passe"
      intro="Choisissez un nouveau mot de passe d’au moins 15 caractères. Toutes vos sessions seront révoquées après la modification."
    >
      {done ? (
        <div className="auth-form">
          <p className="auth-message tosho-feedback is-success" role="status">
            Votre mot de passe a été modifié. Vous devez maintenant vous reconnecter avec votre
            vérification de sécurité.
          </p>
          <button
            className="auth-button auth-button--primary"
            type="button"
            onClick={() => navigate('/')}
          >
            Revenir à la connexion
          </button>
        </div>
      ) : (
        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <label className="auth-field">
            <span>Nouveau mot de passe</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={15}
              required
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          </label>
          <label className="auth-field">
            <span>Confirmer le mot de passe</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={15}
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
            />
          </label>
          {error ? (
            <p className="auth-message tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="auth-button auth-button--primary"
            type="submit"
            disabled={pending || token.length === 0}
          >
            {pending ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function App(): React.JSX.Element {
  const [view, setView] = useState<AuthView>(routeView);
  const [requestExtendedSession, setRequestExtendedSession] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [viewer, setViewer] = useState<SessionViewerResult | null>(null);
  const [sessionChecked, setSessionChecked] = useState(routeView() !== 'LOGIN');
  const [logoutPending, setLogoutPending] = useState(false);

  useEffect(() => {
    const listener = (event: PopStateEvent): void => {
      // Same-session browser visits are restored by the navigation provider.
      // Auth routes and unknown/foreign entries retain the normal session check.
      if (view === 'SIGNED_IN' && viewer && isAuthenticatedNavigation(event.state, viewer)) return;
      setView(routeView());
      setViewer(null);
      setSessionChecked(routeView() !== 'LOGIN');
    };
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, [view, viewer]);

  useEffect(() => {
    if (view !== 'LOGIN' || sessionChecked) return;

    let cancelled = false;
    void (async () => {
      try {
        const session = await authApi.currentSession();
        const csrf = await authApi.csrfToken();
        if (!cancelled) {
          setViewer(session.viewer);
          setCsrfToken(csrf.csrfToken);
          setView('SIGNED_IN');
        }
      } catch {
        // L'absence de session est l'état normal d'un visiteur non authentifié.
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionChecked, view]);

  async function activateAuthenticatedSession(token: string): Promise<void> {
    const session = await authApi.currentSession();
    setViewer(session.viewer);
    setCsrfToken(token);
    setView('SIGNED_IN');
  }

  async function logout(): Promise<void> {
    if (logoutPending) return;
    setLogoutPending(true);
    try {
      await authApi.logout();
      setCsrfToken(null);
      setViewer(null);
      window.history.replaceState({}, '', '/');
      setView('LOGIN');
      setSessionChecked(true);
    } finally {
      setLogoutPending(false);
    }
  }

  if (view === 'TOTP') {
    return (
      <TotpView
        requestExtendedSession={requestExtendedSession}
        onAuthenticated={activateAuthenticatedSession}
        onBack={() => setView('LOGIN')}
      />
    );
  }
  if (view === 'FORGOT') return <ForgotPasswordView />;
  if (view === 'RESET') return <ResetPasswordView />;
  if (view === 'SIGNED_IN' && viewer !== null && csrfToken !== null) {
    return (
      <DashboardShell
        viewer={viewer}
        csrfToken={csrfToken}
        onReauthenticate={() => void logout()}
        onLogout={() => void logout()}
        logoutPending={logoutPending}
      />
    );
  }
  if (view === 'SIGNED_IN' || !sessionChecked) {
    return (
      <AuthShell
        eyebrow="Sécurité du compte"
        title="Vérification de la session"
        intro="Tosho vérifie votre session et vos droits d’accès."
      />
    );
  }
  return (
    <LoginView
      remember={requestExtendedSession}
      onRememberChange={setRequestExtendedSession}
      onTotp={() => setView('TOTP')}
      onAuthenticated={activateAuthenticatedSession}
    />
  );
}
